interface PriceFreshnessProps {
  updatedAt: string | null | undefined;
  source?: string | null;
  confidence?: string | null;
  className?: string;
}

const STALE_AFTER_MS = 48 * 3_600_000;

export function formatPriceAge(iso: string | null | undefined): string {
  if (!iso) return 'never updated';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return mins <= 1 ? 'just now' : `${mins}m ago`;
  const h = Math.floor(diff / 3_600_000);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function isPriceStale(iso: string | null | undefined): boolean {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > STALE_AFTER_MS;
}

type Provenance = {
  label: string;
  detail: string;
  dot: string;
  text: string;
};

/** Resolve source + confidence into a human label, tooltip and dot colour. */
export function priceProvenance(
  source: string | null | undefined,
  confidence: string | null | undefined,
): Provenance {
  const c = confidence || (source === 'justtcg' ? 'sale' : source === 'tcgplayer' ? 'market' : 'estimate');
  if (c === 'sale') {
    return {
      label: 'Last sold',
      detail: 'Most recent completed sale reported by JustTCG',
      dot: 'bg-emerald-500',
      text: 'text-emerald-500',
    };
  }
  if (c === 'market') {
    return {
      label: 'Market',
      detail: 'TCGplayer market price — the rolling average of recent sales',
      dot: 'bg-primary',
      text: 'text-primary',
    };
  }
  return {
    label: 'Estimate',
    detail: 'Published estimate from our backup source — no recent sale data available',
    dot: 'bg-amber-500',
    text: 'text-amber-500',
  };
}

/**
 * Inline price provenance indicator: where the number came from, how confident
 * we are in it, and how old it is.
 */
export default function PriceFreshness({ updatedAt, source, confidence, className = '' }: PriceFreshnessProps) {
  const stale = isPriceStale(updatedAt);
  const age = formatPriceAge(updatedAt);
  const prov = priceProvenance(source, confidence);
  const title = stale
    ? `${prov.detail} · may be out of date, the next sync will refresh it`
    : `${prov.detail} · refreshed ${age}`;

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 text-[10px] leading-none ${
        stale ? 'text-destructive' : 'text-muted-foreground'
      } ${className}`}
    >
      <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${stale ? 'bg-destructive' : prov.dot}`} />
      <span className={stale ? '' : prov.text}>{prov.label}</span>
      <span aria-hidden>·</span>
      {stale ? `out of date · ${age}` : age}
    </span>
  );
}
